import { apiOperacionGETById, EnumBloqueoCapacidad } from '../../api';
import { ApiAllocations } from '../../api/Pioneer/endpoints/allocations/allocations.api';
import { patchCapacity } from '../../api/Pioneer/endpoints/capacities/capacities.api';
import { enumKeyToValue } from '../../helpers';
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper';
import i18n from '../../i18n';
import { ActionIcons, DataTableActionConfiguration } from '../../model';
import { ServiceResponse } from '../../model/serviceResponse';
import { notifyError } from '../../redux';
import {
  allocationsFromApiRepartirModelResponse,
  allocationsFromApiReplicateModelResponse,
  allocationsToApiDistributeAllocationsRequest,
  allocationsToReplicateAllocationRequest,
  operacionFromApi
} from './allocationsEdit.mapper';
import { AllocationModel, OperationModel } from './allocationsEdit.model';

const apiAllocatios = new ApiAllocations();

export const getOperation = async (id: number): Promise<ServiceResponse<any>> => {
  return await apiOperacionGETById(id)
    .then(response => {
      return createSuccessServiceResponse(operacionFromApi(response[0]));
    })
    .catch(error => {
      return createErrorServiceResponse(error);
    });
};

export const updateLockState = async (
  id: number,
  newLockState: EnumBloqueoCapacidad
): Promise<boolean> => {
  return patchCapacity({
    id: id,
    bloqueo: enumKeyToValue(EnumBloqueoCapacidad, newLockState)
  })
    .then(() => {
      return true;
    })
    .catch(() => {
      notifyError(i18n.t('allocationsEdit:notifications.error.updateLockState'));
      return false;
    });
};

export const distributeAllocations = async (
  operation: OperationModel
): Promise<ServiceResponse<any>> => {
  return await apiAllocatios
    .DistributeAllocationsPOST(allocationsToApiDistributeAllocationsRequest(operation.allocations))
    .then(response => {
      return createSuccessServiceResponse(allocationsFromApiRepartirModelResponse(response));
    })
    .catch(error => {
      return createErrorServiceResponse(error);
    });
};

export const replicateAllocations = async (
  allocations: AllocationModel[],
  allocationsModified: AllocationModel[],
  initDate: Date,
  endDate: Date
): Promise<ServiceResponse<any>> => {
  let allocationsRequest = allocationsToReplicateAllocationRequest(allocations);
  let allocationsModifiedRequest = allocationsToReplicateAllocationRequest(allocationsModified);
  return await apiAllocatios
    .ReplicateAllocationDistributionPOST(
      allocationsRequest,
      allocationsModifiedRequest,
      initDate,
      endDate
    )
    .then(response => {
      return createSuccessServiceResponse(allocationsFromApiReplicateModelResponse(response));
    })
    .catch(error => {
      return createErrorServiceResponse(error);
    });
};

export const saveAllocations = async (operation: OperationModel): Promise<ServiceResponse<any>> => {
  return await apiAllocatios
    .DistributeAllocationsAndSavePOST(
      operation!.allocations.reduce((a, b) => a + (b['quantity'] || 0), 0),
      allocationsToApiDistributeAllocationsRequest(operation.allocations)
    )
    .then(response => {
      return createSuccessServiceResponse(allocationsFromApiRepartirModelResponse(response));
    })
    .catch(error => {
      return createErrorServiceResponse(error);
    });
};

//#region Allocation global actions

export const filterAllocationApplicableActionsConfiguration = (
  item: AllocationModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = [];
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.LOCK: {
        if (item.locked === EnumBloqueoCapacidad.BloqueadoUsuario && !item.isModified) {
          applicableActionsConfiguration.push(configuration);
        }
        break;
      }
      case ActionIcons.UNLOCK: {
        if (item.locked === EnumBloqueoCapacidad.Libre && !item.isModified) {
          applicableActionsConfiguration.push(configuration);
        }
        break;
      }
      case ActionIcons.LOCK_DISABLED: {
        if (item.locked === EnumBloqueoCapacidad.BloqueadoBundle && !item.isModified) {
          applicableActionsConfiguration.push(configuration);
        }
        break;
      }
      case ActionIcons.LOCK_TEMPORARY: {
        if (item.isModified) {
          applicableActionsConfiguration.push(configuration);
        }
        break;
      }
    }
  });
  return applicableActionsConfiguration;
};

export const getAllocationActionsConfiguration = (
  hasPermission: boolean
): Array<DataTableActionConfiguration> => {
  return [
    getAllocationLockAction(hasPermission),
    getAllocationUnlockAction(hasPermission),
    getAllocationLockedAction(hasPermission),
    getAllocationLockedTemporaryAction()
  ];
};

const getAllocationLockedTemporaryAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.LOCK_TEMPORARY,
    noHandlerTooltip: 'Bloqueado temporalmente para este reparto'
  };
};

const getAllocationLockedAction = (hasPermission: boolean): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.LOCK_DISABLED,
    noHandlerTooltip: 'Bloqueado',
    hasPermission: hasPermission
  };
};

const getAllocationLockAction = (hasPermission: boolean): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.LOCK,
    tooltip: i18n.t('translation:pages.allocations.dataTable.tooltips.lock'),
    noHandlerTooltip: i18n.t('translation:pages.allocations.dataTable.tooltips.lockDisabled'),
    hasPermission: hasPermission
  };
};

const getAllocationUnlockAction = (hasPermission: boolean): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.UNLOCK,
    tooltip: i18n.t('translation:pages.allocations.dataTable.tooltips.unlock'),
    noHandlerTooltip: i18n.t('translation:pages.allocations.dataTable.tooltips.unlockDisabled'),
    handler: undefined,
    hasPermission: hasPermission
  };
};

//#endregion

//#region Allocation secondary actions

export const getAllocationSecondaryActionsConfiguration =
  (): Array<DataTableActionConfiguration> => {
    return [getAllocationBundleAction()];
  };

const getAllocationBundleAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.BUNDLE
  };
};

export const filterAllocationApplicableSecondaryActionsConfiguration = (
  item: AllocationModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = [];
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.BUNDLE: {
        if (item.bundledParentProduct !== null) {
          configuration.tooltip = i18n.t(
            'translation:pages.allocations.dataTable.tooltips.bundled',
            {
              parentId: item.bundledParentProduct
            }
          );
          configuration.noHandlerTooltip = i18n.t(
            'translation:pages.allocations.dataTable.tooltips.bundled',
            {
              parentId: item.bundledParentProduct
            }
          );
          applicableActionsConfiguration.push(configuration);
        }
        break;
      }
    }
  });
  return applicableActionsConfiguration;
};

//#endregion
